import { z } from "zod";
/**
 * THE ENVIRONMENT VIEW (ADR 0009, commitment 2). The server-derived scene the
 * client draws: a projection that has already collapsed mechanics to physics.
 *
 * THIS MODULE DELIBERATELY IMPORTS NOTHING from primitives.ts, views.ts,
 * check-spec.ts, lead.ts, or any other mechanical module — that absence is
 * load-bearing and lint-enforced (see eslint no-restricted-imports for this
 * file). EnvironmentView may not reference a Band, Position, Effect, Dc,
 * Fidelity, Disposition, SituationPressure, clock/resource fill, lead id,
 * payoff id, or rubric dimension. environment-view.test-d.ts asserts this at
 * the type level; the fixture scan and the rendered-section leak test assert
 * it at runtime. If a field here would require the client to know a band to
 * draw the right thing, the projection is under-cooked — fix the projection,
 * not this file.
 *
 * It is a SIBLING of LearnerViewInPlay, never a field inside it:
 * views.test-d.ts's ForbiddenKeys guard bans ANY number in LearnerViewInPlay
 * absolutely, and this view is legitimately full of physical numbers (hour,
 * minute, light angle, slot indices). See ADR 0009 for the full argument.
 */
/** Namespaced symbol id: pack-qualified, e.g. "core.fig.stand", "hvg.pet.cat". */
export const SymbolRefSchema = z
    .string()
    .regex(/^[a-z][a-z0-9-]*(\.[a-z0-9-]+)+$/, "symbolRef must be a namespaced lowercase dotted id");
/** A lowercase token: pose names, fixture states, prop variants, room ids. */
const TokenSchema = z.string().regex(/^[a-z][a-z0-9-]*$/, "must be a lowercase token");
/**
 * Architectural drawing weights — the section's own grammar (ADR 0009:
 * ghost-weight is how undiscovered areas draw; inked to true weights on
 * discovery). These are drawing terms, not mechanical values.
 */
export const DrawWeightSchema = z.enum(["cut", "obj", "det", "ghost"]);
/**
 * One-shot transient moments. Physical kinds only — a spill, a stilling of
 * the room, an arrival at a threshold — chosen by the projection; the client
 * never learns what caused one.
 */
export const EnvironmentBeatKindSchema = z.enum(["spill", "stilling", "arrival", "departure", "door", "light"]);
const EnvironmentActorSchema = z.strictObject({
    /** Stable within a session so the client can animate continuity between turns. */
    instanceId: z.string().min(1),
    symbolRef: SymbolRefSchema,
    room: TokenSchema,
    /** Index into the room's authored slots (EnvironmentDefinition). */
    slot: z.number().int().min(0),
    /** Pose token, resolved against the symbol's pack (e.g. "stand", "sit", "walk"). */
    pose: TokenSchema,
    facing: z.enum(["left", "right"]),
    /** Props carried/attached, e.g. a folder, a coffee. */
    propRefs: z.array(SymbolRefSchema),
});
const EnvironmentPropSchema = z.strictObject({
    symbolRef: SymbolRefSchema,
    room: TokenSchema,
    /** Index into the room's authored slots. */
    at: z.number().int().min(0),
    /**
     * Physical treatment variant in the symbol's own vocabulary ("crisp",
     * "stack", "torn"...). Treatment words, never a mechanical enum value —
     * the projection owns the mapping (ADR 0009 system grammar).
     */
    variant: TokenSchema.nullable(),
});
const EnvironmentFixtureSchema = z.strictObject({
    /** The fixture instance id from the EnvironmentDefinition ("exit-sign", "wall-clock"). */
    id: TokenSchema,
    symbolRef: SymbolRefSchema,
    /** Physical state token: "on" | "off" | "lit" | "dark" | "ajar" | "open" | "closed" ... */
    state: TokenSchema,
});
const EnvironmentWeightSchema = z.strictObject({
    /** A room or zone id from the EnvironmentDefinition. */
    targetRef: TokenSchema,
    weight: DrawWeightSchema,
});
/**
 * How the scene is lit (ADR 0017). Soft Wash is every ordinary moment; Hard
 * Light is a rare authored emphasis. A presentation word: it says nothing
 * about why, and a night scene is normally still Soft Wash.
 */
export const SceneLightingSchema = z.enum(["soft-wash", "hard-light"]);
export const EnvironmentViewSchema = z.strictObject({
    envId: z.string().min(1),
    /** Diegetic scene label ("Harborlight — Seattle, Tuesday morning"). */
    sceneLabel: z.string().min(1),
    /** The wall clock's face — wall-clock time, not a spend meter. */
    clock: z.strictObject({
        hour: z.number().int().min(0).max(23),
        minute: z.number().int().min(0).max(59),
    }),
    /**
     * The flip calendar and window light. A DATE from the fiction's own
     * calendar (weekday + day-of-month), never an engagement-day index —
     * "TUE 7" is diegetic, "day 2 of 8" is a countdown in a costume.
     * lightAngle is the window light's angle in degrees (0 = dawn horizontal,
     * 90 = noon vertical), a pure function of clock time.
     */
    calendar: z.strictObject({
        weekdayLabel: z.string().min(2).max(3),
        dayNumeral: z.number().int().min(1).max(31),
        lightAngle: z.number().min(0).max(180),
    }),
    actors: z.array(EnvironmentActorSchema),
    props: z.array(EnvironmentPropSchema),
    fixtures: z.array(EnvironmentFixtureSchema),
    weights: z.array(EnvironmentWeightSchema),
    /** At most one transient one-shot moment for this turn, or null. */
    beat: z.strictObject({ transientId: z.string().min(1), kind: EnvironmentBeatKindSchema }).nullable(),
    /**
     * Monotone counter for ambient idle offset. The free-action invariant
     * (ADR 0009): a free action produces an EnvironmentView equal to the
     * previous turn's EXCEPT this field.
     */
    idlePhase: z.number().int().min(0),
    /**
     * SOMEONE ON THE PHONE. A person the story has placed on a call is not in the
     * room: they are drawn as a small shoulders-up inset beside whoever in the
     * room is on the phone with them. Absent on every turn without a call, so
     * every recorded view and fixture stays valid.
     */
    call: z.strictObject({
        /** The absent person's actor id: their first name, as the story says it. */
        who: TokenSchema,
        /** How that person is drawn. The renderer prefers `<symbolRef>-call`, their own on-the-phone portrait, and otherwise crops this painting to the shoulders. */
        symbolRef: SymbolRefSchema,
        /** The actor in the room who is on the phone with them; normally the player. */
        with: z.string().min(1),
    }).optional(),
    /** Absent means Soft Wash, so every recorded view and fixture stays valid. */
    lighting: SceneLightingSchema.optional(),
    /**
     * The section's accessible description: AUTHORED DIEGETIC PROSE assembled
     * from the bible's active tells — never a serialization of this view
     * ("the floor has emptied out; two desks are dark", never "day 6,
     * analysts 1"). Runs through the same leak scan as narration.
     */
    description: z.string().min(1),
});
//# sourceMappingURL=environment-view.js.map